import { useEffect, useState, useCallback } from 'react';

import { Locale } from './locale';
import { getLocalStoragePreferredLocale } from './use-redirect-to-localized-page';

type DefaultTranslationModule = {
  [key in Locale]: any;
};

// List of translation modules
// The values of this Enum can be anything, but they can represent either pages or components
export enum Translatable {
  AddOns = 'addOns',
  Cart = 'cart',
  CartContent = 'cartContent',
  Checkout = 'checkout',
  TicketType = 'ticketType',
}

export function useTranslations<
  P,
  T extends DefaultTranslationModule = DefaultTranslationModule,
>(translatable: Translatable): P | undefined {
  const locale = getLocalStoragePreferredLocale() ?? Locale.EN_US;
  const [translations, setTranslations] = useState<T | null>(null);

  const fetchTranslations = useCallback(async () => {
    try {
      const module = await import(`../data/translations/${translatable}`);
      setTranslations(module.default);
    } catch (error) {
      console.error(`Failed to load translations for ${translatable}:`, error);
    }
  }, [translatable]);

  useEffect(() => {
    fetchTranslations();
  }, [fetchTranslations]);

  // Prioritize returning the translation in the user's preferred locale
  // If it doesn't exist, fallback to the default English translation (EN_US)
  // If neither exists, return the entire translations object or undefined if null
  return translations
    ? translations[locale] ?? translations[Locale.EN_US] ?? translations
    : undefined;
}
